import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, defer, finalize, share } from 'rxjs';
import { DataSubjectLookup, DataSubjectRequest } from './data-subject.models';
import { AuthService } from '../auth/auth.service';
import { Page } from '../page.model';
import {
  ChangeStatePayload,
  EvidencePayload,
  ActionPayload,
  FollowUpPayload,
  Occurrence,
  OccurrenceAttachment,
  OccurrenceAuditEntry,
  OccurrenceEvidence,
  OccurrenceAction,
  OccurrenceFollowUp,
  OccurrenceConversationLink,
  OccurrenceLinks,
  OccurrenceMessage,
  OccurrencePayload,
  OccurrenceStatistics,
  SendMessagePayload,
} from './occurrence.models';

export interface OccurrenceFilters {
  q?: string | null;
  year?: number | null;
  situation?: string | null;
  state?: string | null;
  scope?: string | null;
  /**
   * Os quatro seguintes vieram das estatísticas: cada pedaço de gráfico leva à lista das
   * ocorrências que o compõem, e o recorte tem de ser o mesmo que o gráfico contou.
   */
  subScope?: string | null;
  type?: string | null;
  origin?: string | null;
  nature?: string | null;
  complaintReason?: string | null;
  effectiveness?: string | null;
  /** Verificação da eficácia aplicável (true) ou não aplicável (false). */
  effectivenessApplicable?: boolean | null;
  /** Fechada dentro do prazo de implementação. */
  onTime?: boolean | null;
  page?: number;
  size?: number;
}

@Injectable({ providedIn: 'root' })
export class OccurrenceService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private base(): string {
    return `/api/t/${this.auth.currentTenant()}/ocorrencias`;
  }

  list(filters: OccurrenceFilters = {}): Observable<Page<Occurrence>> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return this.http.get<Page<Occurrence>>(this.base(), { params });
  }

  get(id: number): Observable<Occurrence> {
    return this.http.get<Occurrence>(`${this.base()}/${id}`);
  }

  create(payload: OccurrencePayload): Observable<Occurrence> {
    return this.http.post<Occurrence>(this.base(), payload);
  }

  update(id: number, payload: OccurrencePayload): Observable<Occurrence> {
    return this.http.put<Occurrence>(`${this.base()}/${id}`, payload);
  }

  changeState(id: number, payload: ChangeStatePayload): Observable<Occurrence> {
    return this.http.post<Occurrence>(`${this.base()}/${id}/estado`, payload);
  }

  private readonly inFlight = new Map<string, Observable<unknown>>();

  /**
   * Os componentes da ficha (ações, seguimento, evidências) pedem as mesmas listas ao nascer: quem
   * chega enquanto o pedido está em curso aproveita-o em vez de repetir. Depois de terminado, nada
   * fica em cache — uma releitura depois de gravar vai sempre à API.
   */
  private sharedGet<T>(url: string): Observable<T> {
    return defer(() => {
      let shared = this.inFlight.get(url) as Observable<T> | undefined;
      if (!shared) {
        shared = this.http.get<T>(url).pipe(finalize(() => this.inFlight.delete(url)), share());
        this.inFlight.set(url, shared);
      }
      return shared;
    });
  }

  /** Ações do tratamento — uma lista por ocorrência desde 2026-10-03. */
  actions(id: number): Observable<OccurrenceAction[]> {
    return this.sharedGet<OccurrenceAction[]>(`${this.base()}/${id}/acoes`);
  }

  addAction(id: number, payload: ActionPayload): Observable<OccurrenceAction> {
    return this.http.post<OccurrenceAction>(`${this.base()}/${id}/acoes`, payload);
  }

  updateAction(id: number, actionId: number, payload: ActionPayload): Observable<OccurrenceAction> {
    return this.http.put<OccurrenceAction>(`${this.base()}/${id}/acoes/${actionId}`, payload);
  }

  /** A lista inteira das ações, pela ordem nova. Os números ("#2") não mudam. */
  reorderActions(id: number, actionIds: readonly number[]): Observable<OccurrenceAction[]> {
    return this.http.put<OccurrenceAction[]>(`${this.base()}/${id}/acoes/ordem`, { actionIds });
  }

  deleteAction(id: number, actionId: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/${id}/acoes/${actionId}`);
  }

  /**
   * Eliminação total: a ocorrência e tudo o que está preso a ela. As derivadas seguem o que se
   * decidiu (revisão de 2026-10-15): eliminadas com ela, ou mantidas sem ligação nenhuma à mãe.
   */
  remove(id: number, children: 'ELIMINAR' | 'MANTER' = 'MANTER'): Observable<void> {
    return this.http.delete<void>(`${this.base()}/${id}`, { params: { filhas: children } });
  }

  /**
   * Dá identificador de acompanhamento a quem comunicou e avisa-o por e-mail (modelos
   * aprovados). Pelo site acontece sozinho; a denúncia registada à mão pede-o daqui.
   */
  startTracking(id: number): Observable<Occurrence> {
    return this.http.post<Occurrence>(`${this.base()}/${id}/acompanhamento`, {});
  }

  followUps(id: number): Observable<OccurrenceFollowUp[]> {
    return this.sharedGet<OccurrenceFollowUp[]>(`${this.base()}/${id}/seguimento`);
  }

  addFollowUp(id: number, payload: FollowUpPayload): Observable<OccurrenceFollowUp> {
    return this.http.post<OccurrenceFollowUp>(`${this.base()}/${id}/seguimento`, payload);
  }

  updateFollowUp(id: number, entryId: number, payload: FollowUpPayload): Observable<OccurrenceFollowUp> {
    return this.http.put<OccurrenceFollowUp>(`${this.base()}/${id}/seguimento/${entryId}`, payload);
  }

  /**
   * @param keepLink com a entrada a ter dado origem a outra ocorrência, dizer se a derivada
   *   continua filha desta. A derivada nunca se apaga daqui.
   */
  deleteFollowUp(id: number, entryId: number, keepLink = true): Observable<void> {
    return this.http.delete<void>(`${this.base()}/${id}/seguimento/${entryId}`,
      { params: { manterLigacao: keepLink } });
  }

  /** Thread de e-mails da ocorrência (enviados e recebidos). */
  messages(id: number): Observable<OccurrenceMessage[]> {
    return this.http.get<OccurrenceMessage[]>(`${this.base()}/${id}/comunicacoes`);
  }

  sendMessage(id: number, payload: SendMessagePayload): Observable<OccurrenceMessage> {
    return this.http.post<OccurrenceMessage>(`${this.base()}/${id}/comunicacoes`, payload);
  }

  /** Links externos já abertos nesta ocorrência (para reenviar a quem o perdeu). */
  conversations(id: number): Observable<OccurrenceConversationLink[]> {
    return this.http.get<OccurrenceConversationLink[]>(`${this.base()}/${id}/conversas`);
  }

  /**
   * A ficha RGPD do titular, lida no Essentials. Não fica guardada aqui: é uma janela para o
   * outro lado, aberta enquanto a equipa trata o pedido de direitos.
   */
  dataSubject(id: number): Observable<DataSubjectLookup> {
    return this.http.get<DataSubjectLookup>(`${this.base()}/${id}/titular`);
  }

  /** O rasto do que já se pediu ao Essentials sobre este titular. */
  dataSubjectRequests(id: number): Observable<DataSubjectRequest[]> {
    return this.http.get<DataSubjectRequest[]>(`${this.base()}/${id}/titular/pedidos`);
  }

  /** Pede a eliminação da conta e dos dados. Irreversível do lado do Essentials. */
  requestDataSubjectErasure(id: number, reason: string): Observable<DataSubjectRequest> {
    return this.http.post<DataSubjectRequest>(`${this.base()}/${id}/titular/eliminacao`, { reason });
  }

  evidences(id: number): Observable<OccurrenceEvidence[]> {
    return this.http.get<OccurrenceEvidence[]>(`${this.base()}/${id}/evidencias`);
  }

  addEvidence(id: number, payload: EvidencePayload): Observable<OccurrenceEvidence> {
    return this.http.post<OccurrenceEvidence>(`${this.base()}/${id}/evidencias`, payload);
  }

  /** Corrige uma evidência já registada; os ficheiros anexam-se e removem-se à parte. */
  updateEvidence(id: number, evidenceId: number, payload: EvidencePayload): Observable<OccurrenceEvidence> {
    return this.http.put<OccurrenceEvidence>(`${this.base()}/${id}/evidencias/${evidenceId}`, payload);
  }

  deleteEvidence(id: number, evidenceId: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/${id}/evidencias/${evidenceId}`);
  }

  /** Os ficheiros pertencem sempre a uma entrada de evidência. */
  uploadAttachment(id: number, evidenceId: number, file: File): Observable<OccurrenceAttachment> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<OccurrenceAttachment>(
      `${this.base()}/${id}/anexos?evidenciaId=${evidenceId}`, form);
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/${id}/anexos/${attachmentId}`);
  }

  links(id: number): Observable<OccurrenceLinks> {
    return this.http.get<OccurrenceLinks>(`${this.base()}/${id}/ligacoes`);
  }

  history(id: number): Observable<OccurrenceAuditEntry[]> {
    return this.http.get<OccurrenceAuditEntry[]>(`${this.base()}/${id}/historico`);
  }

  statistics(year?: number | null, scope?: string | null, subScope?: string | null): Observable<OccurrenceStatistics> {
    let params = new HttpParams();
    if (year) params = params.set('year', String(year));
    if (scope) params = params.set('scope', scope);
    if (subScope) params = params.set('subScope', subScope);
    return this.http.get<OccurrenceStatistics>(`${this.base()}/estatisticas`, { params });
  }
}
